import { useEffect, useRef, useCallback } from 'react';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { toggleChat, initChat, pollMessages } from '../../redux/slices/chat';
// components
import ChatButton from './ChatButton';
import ChatWindow from './ChatWindow';
// utils
import { requestNotificationPermission, showDesktopNotification } from '../../utils/desktopNotification';

// ----------------------------------------------------------------------

const POLL_INTERVAL = 3000; // 3 seconds

export default function ChatWidget() {
  const dispatch = useDispatch();
  const { isOpen, unreadCount, sessionId, messages } = useSelector((state) => state.chat);
  const pollIntervalRef = useRef(null);
  const lastMessageTimeRef = useRef(null);
  // null = initial load baseline not yet recorded
  const prevMessageCountRef = useRef(null);

  // Initialize chat on mount
  useEffect(() => {
    dispatch(initChat());
  }, [dispatch]);

  // Fire desktop notification only for messages that arrive after the initial load
  useEffect(() => {
    if (messages.length === 0) return;

    const prev = prevMessageCountRef.current;

    // First population — record baseline, no notification
    if (prev === null) {
      prevMessageCountRef.current = messages.length;
      return;
    }

    prevMessageCountRef.current = messages.length;
    if (messages.length <= prev) return;

    const newMessages = messages.slice(prev);
    const incomingFromSupport = newMessages.filter((m) => m.sender_type === 'operator' || m.sender_type === 'ai');
    if (incomingFromSupport.length > 0 && !isOpen) {
      const last = incomingFromSupport[incomingFromSupport.length - 1];
      const title = last.sender_type === 'operator' ? 'Support Team' : 'Sowan Assistant';
      const body = last.content?.slice(0, 100) || 'New message';
      showDesktopNotification(title, body);
    }
  }, [messages, isOpen]);

  // Track last message time for polling
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      lastMessageTimeRef.current = lastMsg.created_at;
    }
  }, [messages]);

  // Poll whenever there is a session — open or closed (closed = less frequent)
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    const interval = isOpen ? POLL_INTERVAL : POLL_INTERVAL * 2;
    pollIntervalRef.current = setInterval(() => {
      if (lastMessageTimeRef.current) {
        dispatch(pollMessages(lastMessageTimeRef.current));
      }
    }, interval);
  }, [dispatch, isOpen]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [isOpen, sessionId, startPolling, stopPolling]);

  return (
    <>
      {!isOpen && (
        <ChatButton
          onClick={() => {
            requestNotificationPermission();
            dispatch(toggleChat());
          }}
          unreadCount={unreadCount}
        />
      )}
      <ChatWindow open={isOpen} />
    </>
  );
}
